/**
 * AdvancedFiltersDrawer Component
 *
 * Main drawer component for advanced filtering and sorting.
 * Phase 1: Filter builder with field/operator/value rows and paste list support.
 * Phase 2: Sort configuration with drag-to-reorder multi-field sorting.
 *
 * Note: Preview pills are shown on the main toolbar (MetadataTableToolbar),
 * not inside the drawer, to avoid confusing duplication while editing.
 *
 * Future phases:
 * - Phase 4: Saved presets
 */

import { Drawer, Space, Button, Divider, App, Typography, Tooltip } from 'antd'
import { ExclamationCircleOutlined, ReloadOutlined } from '@ant-design/icons'
import { useEffect, useRef } from 'react'
import { useQuery } from '@tanstack/react-query'
import { metadataService } from '@/services/metadataService'
import { FilterBuilder } from './FilterBuilder/FilterBuilder'
import { SortBuilder } from './SortBuilder'
import type { AdvancedFiltersDrawerProps } from './advancedFiltersTypes'
import type { SortField } from './hooks/useSorting'

export function AdvancedFiltersDrawer({
  model,
  open,
  onClose,
  fields: passedFields,
  filters,
  onFiltersChange,
  sortFields,
  onSortFieldsChange,
  onApply,
  defaultFilters,
  defaultSortFields,
  onResetFiltersToDefaults,
  onResetSortToDefaults,
  baseFilters,
  hasLifecycle,
}: AdvancedFiltersDrawerProps) {
  const { modal, message } = App.useApp()

  // Track initial state when drawer opens for dirty state detection
  const initialFiltersRef = useRef<string>('')
  const initialSortFieldsRef = useRef<string>('')

  // Ref to trigger add filter action
  const addFilterRef = useRef<(() => void) | null>(null)

  // Only fetch metadata if not passed from parent
  // When fields are passed, skip fetch (avoids 404 for endpoints like /state-history)
  const { isLoading: isMetadataLoading } = useQuery({
    queryKey: ['metadata', model, 'fields'],
    queryFn: async () => {
      const response = await metadataService.getNonSystemFields(model)
      if (!response.success || !response.data) {
        throw new Error(response.message || 'Failed to fetch metadata')
      }
      return response.data
    },
    staleTime: 1000 * 60 * 60, // 1 hour
    // Skip fetch if fields are passed from parent
    enabled: open && !passedFields,
  })

  // Use passed fields or derive loading state
  const isLoading = passedFields ? false : isMetadataLoading

  // When drawer opens, capture the initial state
  useEffect(() => {
    if (open) {
      initialFiltersRef.current = JSON.stringify(filters)
      initialSortFieldsRef.current = JSON.stringify(sortFields)
    }
  }, [open, filters, sortFields])

  // Check if state has changed (is dirty)
  const isDirty = () => {
    const currentFilters = JSON.stringify(filters)
    const currentSortFields = JSON.stringify(sortFields)
    return (
      currentFilters !== initialFiltersRef.current ||
      currentSortFields !== initialSortFieldsRef.current
    )
  }
  // Check if filters are valid (have field and operator)
  const hasInvalidFilters = filters.some((f) => !f.field || !f.operator)

  // Check if sort fields are valid (have field key)
  const hasInvalidSortFields = sortFields.some((s) => !s.key)

  // Handle close with dirty state check
  const handleClose = () => {
    if (isDirty()) {
      modal.confirm({
        title: 'Discard Changes?',
        icon: <ExclamationCircleOutlined />,
        content: 'You have unsaved changes to filters or sort fields. Are you sure you want to discard them?',
        okText: 'Discard',
        okType: 'danger',
        cancelText: 'Keep Editing',
        onOk: () => {
          onClose()
        },
      })
    } else {
      onClose()
    }
  }

  // Handle apply
  // Note: onApply() from integration hook already closes the drawer via closeDrawer().
  // Do NOT also call onClose() here - that would trigger handleCancel which reverts
  // draft state to applied state with a stale closure (before React processes the
  // state update from applyFilters), causing filters to disappear when reopening.
  const handleApply = () => {
    onApply()
  }

  // Handle sort actions
  const handleAddSort = () => {
    const newSort: SortField = {
      id: crypto.randomUUID(),
      key: '',
      direction: 'desc',
    }
    onSortFieldsChange([...sortFields, newSort])
  }

  const handleRemoveSort = (id: string) => {
    onSortFieldsChange(sortFields.filter((s) => s.id !== id))
  }

  const handleUpdateSort = (id: string, updates: Partial<Omit<SortField, 'id'>>) => {
    onSortFieldsChange(
      sortFields.map((s) => (s.id === id ? { ...s, ...updates } : s))
    )
  }

  const handleReorderSort = (startIndex: number, endIndex: number) => {
    const result = Array.from(sortFields)
    const [removed] = result.splice(startIndex, 1)
    result.splice(endIndex, 0, removed)
    onSortFieldsChange(result)
  }

  // Keyboard shortcuts when drawer is open
  useEffect(() => {
    if (!open) return

    const handleKeyDown = (e: KeyboardEvent) => {
      // Ctrl+Alt+F to add filter
      if (e.ctrlKey && e.altKey && e.key === 'f') {
        e.preventDefault()
        if (addFilterRef.current) {
          addFilterRef.current()
        }
      }

      // Ctrl+Alt+S to add sort
      if (e.ctrlKey && e.altKey && e.key === 's') {
        e.preventDefault()
        handleAddSort()
      }

      // Ctrl+Enter to apply
      if (e.ctrlKey && e.key === 'Enter') {
        e.preventDefault()
        if (!hasInvalidFilters && !hasInvalidSortFields && !isLoading) {
          handleApply()
        }
      }

      // Esc to close (if not already handled by Drawer)
      if (e.key === 'Escape') {
        e.preventDefault()
        handleClose()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [open, hasInvalidFilters, hasInvalidSortFields, isLoading, handleApply, handleClose])

  return (
    <Drawer
      title={
        <Space>
          <span>Advanced Filters & Sort</span>
          {(filters.length > 0 || sortFields.length > 0) && (
            <Typography.Text type="secondary" style={{ fontSize: 14 }}>
              ({filters.length > 0 && `${filters.length} filter${filters.length !== 1 ? 's' : ''}`}
              {filters.length > 0 && sortFields.length > 0 && ', '}
              {sortFields.length > 0 && `${sortFields.length} sort${sortFields.length !== 1 ? 's' : ''}`})
            </Typography.Text>
          )}
        </Space>
      }
      placement="right"
      width={700}
      open={open}
      onClose={handleClose}
      footer={
        <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
          <Button onClick={handleClose}>Cancel</Button>
          <Button
            type="primary"
            onClick={handleApply}
            disabled={hasInvalidFilters || hasInvalidSortFields || isLoading}
            loading={isLoading}
          >
            Apply
          </Button>
        </Space>
      }
    >
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        {/* Filter Builder Section */}
        <div>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 8,
            paddingBottom: 8,
            borderBottom: '1px solid #f0f0f0'
          }}>
            <Typography.Title level={5} style={{ margin: 0 }}>Filters</Typography.Title>
            <Space size="small">
              <Tooltip title="Clear all filters">
                <Button
                  type="text"
                  onClick={() => {
                    onFiltersChange([])
                    message.success('Filters cleared')
                  }}
                  disabled={filters.length === 0}
                  size="small"
                  style={{ fontWeight: 500 }}
                >
                  Clear
                </Button>
              </Tooltip>
              {defaultFilters?.length ? (
                <Tooltip title="Restore default filters for this view">
                  <Button
                    type="text"
                    icon={<ReloadOutlined />}
                    onClick={() => {
                      if (onResetFiltersToDefaults) {
                        onResetFiltersToDefaults()
                        message.success('Filters reset to defaults')
                      }
                    }}
                    size="small"
                    style={{ fontWeight: 500 }}
                  >
                    Reset to Defaults
                  </Button>
                </Tooltip>
              ) : null}
            </Space>
          </div>
          <Typography.Text type="secondary" style={{ fontSize: 13, display: 'block', marginBottom: 12 }}>
            Add conditions to filter results. All filters are combined with AND logic.{' '}
            <Typography.Text keyboard style={{ marginLeft: 4 }}>Ctrl+Alt+F</Typography.Text>
          </Typography.Text>
          <FilterBuilder
            model={model}
            fields={passedFields}
            filters={filters}
            onChange={onFiltersChange}
            onAddFilterRef={addFilterRef}
            baseFilters={baseFilters}
            hasLifecycle={hasLifecycle}
          />
        </div>

        {/* Divider */}
        <Divider style={{ margin: '24px 0' }} />

        {/* Sort Configuration Section */}
        <div>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 8,
            paddingBottom: 8,
            borderBottom: '1px solid #f0f0f0'
          }}>
            <Typography.Title level={5} style={{ margin: 0 }}>Sort</Typography.Title>
            <Space size="small">
              <Tooltip title="Clear all sort fields">
                <Button
                  type="text"
                  onClick={() => {
                    onSortFieldsChange([])
                    message.success('Sort cleared')
                  }}
                  disabled={sortFields.length === 0}
                  size="small"
                  style={{ fontWeight: 500 }}
                >
                  Clear
                </Button>
              </Tooltip>
              {defaultSortFields?.length ? (
                <Tooltip title="Restore default sort fields for this view">
                  <Button
                    type="text"
                    icon={<ReloadOutlined />}
                    onClick={() => {
                      if (onResetSortToDefaults) {
                        onResetSortToDefaults()
                        message.success('Sort reset to defaults')
                      }
                    }}
                    size="small"
                    style={{ fontWeight: 500 }}
                  >
                    Reset to Defaults
                  </Button>
                </Tooltip>
              ) : null}
            </Space>
          </div>
          <Typography.Text type="secondary" style={{ fontSize: 13, display: 'block', marginBottom: 12 }}>
            Define sort order for results. First field has highest priority, then second, and so on.{' '}
            <Typography.Text keyboard style={{ marginLeft: 4 }}>Ctrl+Alt+S</Typography.Text>
          </Typography.Text>
          <SortBuilder
            model={model}
            fields={passedFields}
            sortFields={sortFields}
            onAddSort={handleAddSort}
            onRemoveSort={handleRemoveSort}
            onUpdateSort={handleUpdateSort}
            onReorderSort={handleReorderSort}
          />
        </div>
      </Space>
    </Drawer>
  )
}
